import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getDatabase, ref, get, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { app } from '../../firebase';
import { toast } from 'react-toastify';
import {
  FaHeart, FaCrown, FaRegHeart, FaSearch, FaBriefcase,
  FaMapMarkerAlt, FaEye, FaFilter, FaTimes,
  FaExclamationTriangle, FaLock
} from 'react-icons/fa';

// List of parishes in Jamaica
const PARISHES = [
  "Clarendon", "Hanover", "Kingston", "Manchester", "Portland",
  "Saint Andrew", "Saint Ann", "Saint Catherine", "Saint Elizabeth",
  "Saint James", "Saint Mary", "Saint Thomas", "Trelawny", "Westmoreland"
];

// Employment types
const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "temporary", label: "Temporary" },
  { value: "internship", label: "Internship" },
  { value: "volunteer", label: "Volunteer" },
  { value: "freelancer", label: "Freelance" }
];

// Comprehensive list of industries
const INDUSTRIES_LIST = [
  "Information Technology", "Software Development", "Cybersecurity", "Data Science & Analytics",
  "Artificial Intelligence & Machine Learning", "Cloud Computing", "Web Development",
  "Mobile App Development", "IT Support & Services", "Telecommunications",
  "Banking & Financial Services", "Accounting & Auditing", "Insurance",
  "Investment & Asset Management", "Real Estate", "Consulting", "Human Resources",
  "Marketing & Advertising", "Public Relations", "Sales & Business Development",
  "Healthcare & Medical Services", "Nursing", "Pharmaceuticals", "Biotechnology",
  "Mental Health Services", "Dental Services", "Medical Research", "Health Administration",
  "Education & Teaching", "Higher Education", "Early Childhood Education",
  "Corporate Training", "E-Learning & EdTech", "Special Education",
  "Civil Engineering", "Mechanical Engineering", "Electrical Engineering",
  "Chemical Engineering", "Manufacturing", "Automotive", "Aerospace", "Construction",
  "Graphic Design", "Film & Video Production", "Photography", "Music & Audio",
  "Writing & Editing", "Journalism", "Broadcasting", "Gaming & Interactive Media",
  "Hotel & Resort Management", "Restaurant & Food Service", "Travel & Tourism",
  "Event Planning", "Cruise & Maritime",
  "Retail Management", "E-Commerce", "Customer Service", "Fashion & Apparel", "Beauty & Cosmetics",
  "Agriculture & Farming", "Environmental Services", "Renewable Energy",
  "Wildlife & Conservation", "Forestry", "Fishing & Aquaculture",
  "Legal Services", "Government & Public Administration", "Law Enforcement",
  "Military & Defense", "Non-Profit & NGO", "Social Services",
  "Logistics & Supply Chain", "Shipping & Freight", "Aviation",
  "Public Transportation", "Warehousing & Distribution",
  "Plumbing", "Electrical Work", "Carpentry", "HVAC", "Welding",
  "Masonry", "Auto Mechanics", "General Contracting",
  "Security Services", "Domestic Work & Housekeeping", "Childcare & Nanny Services",
  "Pet Care & Veterinary", "Fitness & Wellness", "Sports & Recreation",
  "Religious & Spiritual Services", "Other"
];

// Image Modal Component for enlarging photos
const ImageModal = ({ isOpen, onClose, imageUrl, altText }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative flex flex-col items-center">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white text-4xl hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          ×
        </button>
        <img
          src={imageUrl}
          alt={altText}
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

// Table row component
const CandidateRow = ({ candidate, isFavorite, onToggleFavorite, onPhotoClick }) => {
  // Format employment type for display
  const formatEmploymentType = (type) => {
    if (!type) return 'Not specified';
    const typeMap = {
      'full-time': 'Full-Time',
      'part-time': 'Part-Time',
      'contract': 'Contract',
      'temporary': 'Temporary',
      'internship': 'Internship',
      'volunteer': 'Volunteer',
      'freelancer': 'Freelance'
    };
    return typeMap[type] || type;
  };

  // Map education level values to display labels
  const EDUCATION_LABELS = {
    'no-formal-education': 'No Formal Education',
    'primary': 'Primary / Basic School',
    'secondary-csec': 'Secondary (CXC/CSEC)',
    'secondary-cape': 'CAPE / A-Level',
    'vocational': 'Vocational / Technical Cert.',
    'associate': 'Associate Degree',
    'bachelor': "Bachelor's Degree",
    'postgraduate-diploma': 'Postgrad. Certificate / Diploma',
    'master': "Master's Degree",
    'doctoral': 'Doctoral Degree (PhD)'
  };

  // Get industries from candidate data, profile, or skills
  const getIndustries = () => {
    // First check top-level industriesOfInterest
    if (candidate.industriesOfInterest && candidate.industriesOfInterest.length > 0) {
      return candidate.industriesOfInterest.slice(0, 2).join(', ');
    }
    // Then check profile industries
    if (candidate.profile?.industries && candidate.profile.industries.length > 0) {
      return candidate.profile.industries.slice(0, 2).join(', ');
    }
    // Fallback to skills
    if (candidate.profile?.skills && candidate.profile.skills.length > 0) {
      return candidate.profile.skills.slice(0, 2).join(', ');
    }
    return 'Not specified';
  };

  // Truncate about me text
  const truncateAboutMe = (text, maxLength = 100) => {
    if (!text) return 'No description available';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <tr className="border-b hover:bg-gray-50 transition-colors">
      {/* Profile Photo */}
      <td className="px-4 py-4">
        {candidate.profile?.photo ? (
          <div className="relative group">
            <img
              src={candidate.profile.photo.url}
              alt={`${candidate.firstName} ${candidate.lastName}`}
              className="w-12 h-12 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPhotoClick(candidate);
              }}
              title="Click to enlarge"
            />
          </div>
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-lg font-semibold">
              {candidate.firstName?.[0]}{candidate.lastName?.[0]}
            </span>
          </div>
        )}
      </td>

      {/* Name */}
      <td className="px-4 py-4">
        <Link
          to={`/employer/resumes/${candidate.id}`}
          className="font-semibold text-blue-950 hover:text-blue-700 transition-colors"
        >
          {candidate.firstName} {candidate.lastName}
        </Link>
        {candidate.profile?.title && (
          <p className="text-xs text-gray-500 mt-1">{candidate.profile.title}</p>
        )}
        {candidate.levelOfEducation && (
          <p className="text-xs text-blue-700 mt-1">{EDUCATION_LABELS[candidate.levelOfEducation] || candidate.levelOfEducation}</p>
        )}
      </td>

      {/* Location */}
      <td className="px-4 py-4 text-sm text-gray-700">
        <div className="flex items-center">
          <FaMapMarkerAlt className="mr-2 text-blue-950 flex-shrink-0" size={14} />
          <span>{candidate.parish || 'Not specified'}</span>
        </div>
      </td>

      {/* Employment Type */}
      <td className="px-4 py-4 text-sm text-gray-700">
        <div className="flex items-center">
          <FaBriefcase className="mr-2 text-blue-950 flex-shrink-0" size={14} />
          <span>{formatEmploymentType(candidate.employmentType)}</span>
        </div>
      </td>

      {/* Industries/Skills */}
      <td className="px-4 py-4 text-sm text-gray-700">
        <span className="line-clamp-2">{getIndustries()}</span>
      </td>

      {/* About Me Preview */}
      <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">
        <p className="line-clamp-2">{truncateAboutMe(candidate.profile?.aboutMe)}</p>
      </td>

      {/* Actions */}
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <Link
            to={`/employer/resumes/${candidate.id}`}
            className="inline-flex items-center px-3 py-2 bg-blue-950 text-white text-sm rounded-md hover:bg-blue-800 transition-colors"
            title="View Profile"
          >
            <FaEye className="mr-1" size={14} />
            View
          </Link>
          <button
            onClick={() => onToggleFavorite(candidate.id)}
            className={`p-2 rounded-md transition-colors ${
              isFavorite
                ? 'text-red-500 hover:bg-red-50'
                : 'text-gray-400 hover:bg-gray-100 hover:text-red-500'
            }`}
            title={isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            {isFavorite ? <FaHeart size={18} /> : <FaRegHeart size={18} />}
          </button>
        </div>
      </td>
    </tr>
  );
};

// No Subscription component
const NoSubscription = ({ navigate }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-blue-950">Candidate Database</h2>
      </div>

      {/* Subscription Status Alert */}
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
        <div className="flex items-start">
          <FaExclamationTriangle className="text-red-500 mt-0.5 mr-3" />
          <div>
            <p className="font-bold text-red-700">No Active Subscription</p>
            <p className="text-red-600">You currently don't have an active subscription plan.</p>
          </div>
        </div>
      </div>

      <div className="text-center py-10">
        <div className="mb-6 flex flex-col items-center">
          <FaLock className="text-gray-400 text-6xl mb-4" />
          <h2 className="text-xl font-bold text-blue-950 mb-2">Access to Candidate Database Locked</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            To view our full database of qualified candidates, please subscribe to one of our employer plans.
            A subscription gives you access to candidate profiles, resumes, and contact information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/pricing')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              View Subscription Plans
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Expired Subscription component
const ExpiredSubscription = ({ navigate, endDate, subscription }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-blue-950">Candidate Database</h2>
      </div>

      {/* Subscription Status Alert */}
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
        <div className="flex items-start">
          <FaExclamationTriangle className="text-yellow-500 mt-0.5 mr-3" />
          <div>
            <p className="font-bold text-yellow-800">Subscription Expired</p>
            <p className="text-yellow-700">
              Your {subscription?.package?.name || "subscription"} plan expired on {new Date(endDate).toLocaleDateString()}.
            </p>
          </div>
        </div>
      </div>

      <div className="text-center py-10">
        <div className="mb-6 flex flex-col items-center">
          <FaLock className="text-gray-400 text-6xl mb-4" />
          <h2 className="text-xl font-bold text-blue-950 mb-2">Access to Candidate Database Locked</h2>
          <p className="text-gray-600 mb-2 max-w-2xl mx-auto">
            Your access to our candidate database has expired.
          </p>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Renew your subscription to continue accessing candidate profiles, resumes, and contact information.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate('/pricing')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Renew Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CandidateResumes = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    hasActiveSubscription: false,
    subscription: null,
    isLoading: true
  });

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    industry: '',
    gender: '',
    parish: '',
    employmentType: '',
    labourAvailability: '',
    levelOfEducation: ''
  });

  // Industry dropdown state
  const [industrySearchTerm, setIndustrySearchTerm] = useState('');
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const auth = getAuth(app);
  const db = getDatabase(app);
  const navigate = useNavigate();

  // Filter industries based on search
  const filteredIndustries = INDUSTRIES_LIST.filter(industry =>
    industry.toLowerCase().includes(industrySearchTerm.toLowerCase())
  );

  // Get unique industries from candidates for the filter
  const availableIndustries = useMemo(() => {
    const industries = new Set();
    candidates.forEach(candidate => {
      if (candidate.industriesOfInterest && Array.isArray(candidate.industriesOfInterest)) {
        candidate.industriesOfInterest.forEach(ind => industries.add(ind));
      }
    });
    return Array.from(industries).sort();
  }, [candidates]);

  // NEW: Function to calculate subscription from payments (matching Pricing/EmployerProfile logic)
  const calculateSubscriptionFromPayments = useCallback(async (userId) => {
    try {
      // Get the user's payment history
      const paymentsRef = ref(db, `payments/${userId}`);
      const paymentsSnapshot = await get(paymentsRef);

      if (!paymentsSnapshot.exists()) {
        return null;
      }

      const payments = paymentsSnapshot.val();

      // Get the most recent completed payment
      const completedPayments = Object.entries(payments)
        .filter(([, payment]) => payment.status === 'approved')
        .map(([id, payment]) => ({
          id,
          ...payment,
          createdAt: new Date(payment.createdAt)
        }))
        .sort((a, b) => b.createdAt - a.createdAt);

      if (completedPayments.length === 0) {
        return null;
      }

      const latestPayment = completedPayments[0];

      // Get package details
      const packageRef = ref(db, `packages/${latestPayment.packageId}`);
      const packageSnapshot = await get(packageRef);

      if (!packageSnapshot.exists()) {
        // Package doesn't exist, but we have payment data
        const paymentDate = latestPayment.createdAt;
        const subscriptionEndDate = new Date(paymentDate);
        subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 30); // Default 30 days

        const now = new Date();
        const isActive = subscriptionEndDate > now;

        return {
          package: {
            id: latestPayment.packageId,
            name: latestPayment.packageName || 'Package',
            price: latestPayment.amount,
            features: {}
          },
          startDate: paymentDate.toISOString(),
          endDate: subscriptionEndDate.toISOString(),
          status: isActive ? 'active' : 'expired',
          paymentStatus: 'confirmed'
        };
      }

      const packageData = packageSnapshot.val();

      // Calculate subscription end date based on payment date + package duration
      const paymentDate = latestPayment.createdAt;
      const subscriptionEndDate = new Date(paymentDate);
      subscriptionEndDate.setDate(subscriptionEndDate.getDate() + (packageData.duration || 30));

      const now = new Date();
      const isActive = subscriptionEndDate > now;

      return {
        package: {
          id: latestPayment.packageId,
          name: packageData.name,
          price: latestPayment.amount,
          duration: packageData.duration,
          features: packageData.features || {},
          jobPostLimit: packageData.jobPostLimit
        },
        startDate: paymentDate.toISOString(),
        endDate: subscriptionEndDate.toISOString(),
        status: isActive ? 'active' : 'expired',
        paymentStatus: 'confirmed'
      };

    } catch (error) {
      console.error('Error calculating subscription from payments:', error);
      return null;
    }
  }, [db]);

  const checkSubscription = useCallback(async () => {
    try {
      if (!auth.currentUser?.uid) {
        setSubscriptionStatus({
          hasActiveSubscription: false,
          subscription: null,
          isLoading: false,
          error: 'User not authenticated'
        });
        return false;
      }

      // Primary: check the employer's subscription node set by handlePaymentCompletion.
      // Payments start as 'pending' until admin approves, so payment-based calculation
      // alone is unreliable — the subscription node is the authoritative source.
      const subSnapshot = await get(ref(db, `employers/${auth.currentUser.uid}/subscription`));
      if (subSnapshot.exists()) {
        const subData = subSnapshot.val();
        const now = new Date();
        const endDate = subData.endDate ? new Date(subData.endDate) : null;
        const isActive = subData.status === 'active' && endDate && endDate > now;

        if (isActive) {
          const subscription = { ...subData, paymentStatus: 'confirmed' };
          setSubscriptionStatus({ hasActiveSubscription: true, subscription, isLoading: false });
          return true;
        }
      }

      // Fallback: derive subscription from approved payment records
      const subscription = await calculateSubscriptionFromPayments(auth.currentUser.uid);
      const hasActiveSubscription =
        subscription?.status === 'active' &&
        subscription?.paymentStatus === 'confirmed' &&
        (subscription?.endDate ? new Date(subscription.endDate) > new Date() : false);

      setSubscriptionStatus({ hasActiveSubscription, subscription, isLoading: false });
      return hasActiveSubscription;
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionStatus({
        hasActiveSubscription: false,
        subscription: null,
        isLoading: false,
        error: error.message
      });
      return false;
    }
  }, [auth.currentUser?.uid, db, calculateSubscriptionFromPayments]);

  const fetchCandidates = useCallback(async () => {
    try {
      const candidatesRef = ref(db, 'candidates');
      const snapshot = await get(candidatesRef);

      if (snapshot.exists()) {
        const candidatesData = Object.entries(snapshot.val())
          .map(([id, data]) => ({
            id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone || '',
            employmentType: data.employmentType || '',
            parish: data.parish || '',
            profile: data.profile || {},
            isPublic: data.isPublic,
            status: data.status || data.profileStatus,
            ...data
          }))
          .filter(candidate => {
            // Filtering criteria for employer candidate search:
            // 1. Candidate must have a profile
            // 2. Candidate must have uploaded a resume
            // 3. Profile Status must be 'active' (based on login activity - system-managed)
            // 4. Visibility must be 'public' (user-controlled preference)
            const hasProfile = !!candidate.profile;
            const hasResume = candidate.profile && candidate.profile.resume && candidate.profile.resume.url;
            const isActive = candidate.profileStatus === 'active';  // System status
            const isPublic = candidate.isPublic === true;           // User visibility

            // Must be BOTH active AND public to appear in search
            // Inactive profiles (90+ days no login) are hidden even if set to public
            return hasProfile && hasResume && isActive && isPublic;
          });

        console.log('Filtered candidates:', candidatesData.length);
        setCandidates(candidatesData);
      } else {
        console.log('No candidates found in database');
        setCandidates([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Error loading candidates');
      setLoading(false);
    }
  }, [db]);

  const fetchFavorites = useCallback(async () => {
    try {
      const favoritesRef = ref(db, `employers/${auth.currentUser.uid}/favorites`);
      const snapshot = await get(favoritesRef);

      if (snapshot.exists()) {
        setFavorites(Object.keys(snapshot.val()));
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  }, [auth.currentUser?.uid, db]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check subscription status
        const hasActiveSubscription = await checkSubscription();

        // Only fetch candidates if the employer has an active subscription
        if (hasActiveSubscription) {
          await Promise.all([fetchCandidates(), fetchFavorites()]);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error loading data');
        setLoading(false);
      }
    };

    if (auth.currentUser?.uid) {
      fetchData();
    } else {
      setLoading(false);
      setSubscriptionStatus({
        hasActiveSubscription: false,
        subscription: null,
        isLoading: false,
        error: 'User not authenticated'
      });
    }
  }, [auth.currentUser?.uid, db, fetchCandidates, fetchFavorites, checkSubscription]);

  const toggleFavorite = async (candidateId) => {
    try {
      if (favorites.includes(candidateId)) {
        await update(ref(db, `employers/${auth.currentUser.uid}/favorites`), {
          [candidateId]: null
        });
        setFavorites(favorites.filter(id => id !== candidateId));
        toast.success('Removed from favorites');
      } else {
        await update(ref(db, `employers/${auth.currentUser.uid}/favorites`), {
          [candidateId]: true
        });
        setFavorites([...favorites, candidateId]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      toast.error('Error updating favorites');
    }
  };

  const handlePhotoClick = (candidate) => {
    setSelectedPhoto({
      url: candidate.profile.photo.url,
      name: `${candidate.firstName} ${candidate.lastName}`
    });
    setIsImageModalOpen(true);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      industry: '',
      gender: '',
      parish: '',
      employmentType: '',
      labourAvailability: '',
      levelOfEducation: ''
    });
    setSearchTerm('');
    setIndustrySearchTerm('');
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '') || searchTerm !== '';

  // Comprehensive search function that searches across all candidate fields
  const searchCandidate = (candidate, searchTerm) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    // Helper function to safely search in a field
    const matchField = (field) => {
      return field && field.toString().toLowerCase().includes(searchLower);
    };

    // Helper function to search in arrays
    const matchArray = (arr) => {
      return arr && Array.isArray(arr) && arr.some(item =>
        item && item.toString().toLowerCase().includes(searchLower)
      );
    };

    // Basic fields
    if (matchField(candidate.firstName)) return true;
    if (matchField(candidate.lastName)) return true;
    if (matchField(candidate.middleInitial)) return true;
    if (matchField(candidate.email)) return true;
    if (matchField(candidate.phone)) return true;
    if (matchField(candidate.address)) return true;
    if (matchField(candidate.parish)) return true;
    if (matchField(candidate.employmentType)) return true;
    if (matchField(candidate.labourAvailability)) return true;
    if (matchField(candidate.objectives)) return true;
    if (matchArray(candidate.industriesOfInterest)) return true;

    // Profile fields
    if (candidate.profile) {
      if (matchField(candidate.profile.aboutMe)) return true;
      if (matchField(candidate.profile.title)) return true;
      if (matchField(candidate.profile.summary)) return true;
      if (matchField(candidate.profile.objective)) return true;
      if (matchField(candidate.profile.professionalSummary)) return true;

      // Arrays in profile
      if (matchArray(candidate.profile.skills)) return true;
      if (matchArray(candidate.profile.industries)) return true;
      if (matchArray(candidate.profile.languages)) return true;
      if (matchArray(candidate.profile.certifications)) return true;

      // Experience
      if (candidate.profile.experience && Array.isArray(candidate.profile.experience)) {
        for (const exp of candidate.profile.experience) {
          if (matchField(exp.title)) return true;
          if (matchField(exp.company)) return true;
          if (matchField(exp.description)) return true;
          if (matchField(exp.location)) return true;
          if (matchArray(exp.responsibilities)) return true;
        }
      }

      // Education
      if (candidate.profile.education && Array.isArray(candidate.profile.education)) {
        for (const edu of candidate.profile.education) {
          if (matchField(edu.school)) return true;
          if (matchField(edu.degree)) return true;
          if (matchField(edu.field)) return true;
          if (matchField(edu.fieldOfStudy)) return true;
          if (matchField(edu.description)) return true;
        }
      }

      // Projects
      if (candidate.profile.projects && Array.isArray(candidate.profile.projects)) {
        for (const proj of candidate.profile.projects) {
          if (matchField(proj.name)) return true;
          if (matchField(proj.description)) return true;
          if (matchArray(proj.technologies)) return true;
        }
      }

      // Awards/Achievements
      if (matchArray(candidate.profile.awards)) return true;
      if (matchArray(candidate.profile.achievements)) return true;

      // References
      if (candidate.profile.references && Array.isArray(candidate.profile.references)) {
        for (const refItem of candidate.profile.references) {
          if (matchField(refItem.name)) return true;
          if (matchField(refItem.company)) return true;
          if (matchField(refItem.position)) return true;
        }
      }
    }

    return false;
  };

  // Apply filters to candidate
  const applyFilters = (candidate) => {
    // Industry filter
    if (filters.industry) {
      const hasIndustry = candidate.industriesOfInterest?.includes(filters.industry);
      if (!hasIndustry) return false;
    }

    // Gender filter
    if (filters.gender) {
      if (candidate.gender?.toLowerCase() !== filters.gender.toLowerCase()) return false;
    }

    // Parish filter
    if (filters.parish) {
      // Handle different parish name formats (e.g., "St. Andrew" vs "Saint Andrew")
      const candidateParish = candidate.parish?.toLowerCase().replace(/st\./g, 'saint').replace(/\s+/g, ' ').trim();
      const filterParish = filters.parish.toLowerCase().replace(/st\./g, 'saint').replace(/\s+/g, ' ').trim();
      if (candidateParish !== filterParish) return false;
    }

    // Employment type filter
    if (filters.employmentType) {
      if (candidate.employmentType?.toLowerCase() !== filters.employmentType.toLowerCase()) return false;
    }

    // Labour availability filter
    if (filters.labourAvailability) {
      if (candidate.labourAvailability?.toLowerCase() !== filters.labourAvailability.toLowerCase()) return false;
    }

    // Level of education filter
    if (filters.levelOfEducation) {
      if (candidate.levelOfEducation !== filters.levelOfEducation) return false;
    }

    return true;
  };

  // Filter and sort candidates alphabetically by name
  const filteredCandidates = candidates
    .filter(candidate => searchCandidate(candidate, searchTerm))
    .filter(candidate => applyFilters(candidate))
    .sort((a, b) => {
      // Sort alphabetically by last name, then first name
      const lastNameCompare = a.lastName.localeCompare(b.lastName);
      if (lastNameCompare !== 0) return lastNameCompare;
      return a.firstName.localeCompare(b.firstName);
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading || subscriptionStatus.isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-950"></div>
      </div>
    );
  }

  // If no active subscription, show subscription message
  if (!subscriptionStatus.hasActiveSubscription) {
    // Check if subscription exists but has expired
    if (subscriptionStatus.subscription?.endDate && new Date(subscriptionStatus.subscription.endDate) < new Date()) {
      return <ExpiredSubscription
        navigate={navigate}
        endDate={subscriptionStatus.subscription.endDate}
        subscription={subscriptionStatus.subscription}
      />;
    }

    // Otherwise, show generic no subscription message
    return <NoSubscription navigate={navigate} />;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-blue-950 mb-4">All Candidates</h2>

        {/* Subscription status banner */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-center">
          <FaCrown className="text-yellow-500 mr-3 text-xl" />
          <div>
            <p className="text-sm text-blue-600">
              Valid until: {new Date(subscriptionStatus.subscription.endDate).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by name, location, experience, interest, skills, industry, etc..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-950 text-white border-blue-950'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FaFilter className="mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-white text-blue-950 text-xs px-2 py-0.5 rounded-full">
                {Object.values(filters).filter(v => v !== '').length + (searchTerm ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4 border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-700">Filter Candidates</h3>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-600 hover:text-red-800 flex items-center"
                >
                  <FaTimes className="mr-1" /> Clear All Filters
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
              {/* Industry Filter */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search industries..."
                    value={filters.industry || industrySearchTerm}
                    onChange={(e) => {
                      setIndustrySearchTerm(e.target.value);
                      setIsIndustryDropdownOpen(true);
                      if (!e.target.value) {
                        handleFilterChange('industry', '');
                      }
                    }}
                    onFocus={() => setIsIndustryDropdownOpen(true)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                  {filters.industry && (
                    <button
                      onClick={() => {
                        handleFilterChange('industry', '');
                        setIndustrySearchTerm('');
                      }}
                      className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}

                  {isIndustryDropdownOpen && (
                    <>
                      <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {(industrySearchTerm ? filteredIndustries : availableIndustries).length === 0 ? (
                          <div className="px-3 py-2 text-gray-500 text-sm">No industries found</div>
                        ) : (
                          (industrySearchTerm ? filteredIndustries : availableIndustries).map((industry) => (
                            <div
                              key={industry}
                              onClick={() => {
                                handleFilterChange('industry', industry);
                                setIndustrySearchTerm('');
                                setIsIndustryDropdownOpen(false);
                              }}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                                filters.industry === industry ? 'bg-blue-50 text-blue-700' : ''
                              }`}
                            >
                              {industry}
                            </div>
                          ))
                        )}
                      </div>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsIndustryDropdownOpen(false)}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              {/* Parish Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location (Parish)</label>
                <select
                  value={filters.parish}
                  onChange={(e) => handleFilterChange('parish', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Parishes</option>
                  {PARISHES.map(parish => (
                    <option key={parish} value={parish}>{parish}</option>
                  ))}
                </select>
              </div>

              {/* Employment Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                <select
                  value={filters.employmentType}
                  onChange={(e) => handleFilterChange('employmentType', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Types</option>
                  {EMPLOYMENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Labour Availability Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strenuous Activities</label>
                <select
                  value={filters.labourAvailability}
                  onChange={(e) => handleFilterChange('labourAvailability', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All</option>
                  <option value="yes">Available</option>
                  <option value="no">Not Available</option>
                </select>
              </div>

              {/* Level of Education Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Level of Education</label>
                <select
                  value={filters.levelOfEducation}
                  onChange={(e) => handleFilterChange('levelOfEducation', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                >
                  <option value="">All Education Levels</option>
                  <option value="no-formal-education">No Formal Education</option>
                  <option value="primary">Primary / Basic School</option>
                  <option value="secondary-csec">Secondary (CXC/CSEC)</option>
                  <option value="secondary-cape">CAPE / A-Level</option>
                  <option value="vocational">Vocational / Technical Certificate</option>
                  <option value="associate">Associate Degree</option>
                  <option value="bachelor">Bachelor's Degree</option>
                  <option value="postgraduate-diploma">Postgraduate Certificate / Diploma</option>
                  <option value="master">Master's Degree</option>
                  <option value="doctoral">Doctoral Degree (PhD)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Results count and items per page */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredCandidates.length)} of {filteredCandidates.length} candidates
            {hasActiveFilters && ' (filtered)'}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1.5 border rounded-md text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600">per page</span>
          </div>
        </div>

        {filteredCandidates.length === 0 ? (
          <div className="text-center py-8">
            <FaSearch className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Candidates Found
            </h3>
            <p className="text-gray-500">
              {hasActiveFilters
                ? "No candidates match your search criteria. Try different keywords or adjust your filters."
                : "No candidates are available at the moment."}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Photo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Employment Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Industries/Skills</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">About</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.map((candidate) => (
                    <CandidateRow
                      key={candidate.id}
                      candidate={candidate}
                      isFavorite={favorites.includes(candidate.id)}
                      onToggleFavorite={toggleFavorite}
                      onPhotoClick={handlePhotoClick}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t gap-4">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Prev
                  </button>

                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-500">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-sm rounded-md ${
                          currentPage === page
                            ? 'bg-blue-950 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 text-sm rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Modal for enlarged profile photos */}
      {selectedPhoto && (
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => {
            setIsImageModalOpen(false);
            setSelectedPhoto(null);
          }}
          imageUrl={selectedPhoto.url}
          altText={selectedPhoto.name}
        />
      )}
    </div>
  );
};

export default CandidateResumes;
